import Storage from './storage.js';
import Logger from './logger.js';
import _ from './translate.js';

class Products {
    data = null;
    // products = null;
    // attributes = null;
    // retouch = null;
    // resolution = null;
    loading = false;
    cacheKeyBase = 'mscp_products_cache2';
    cacheTtlMs = 60 * 1000 // TODO: temp 1 minute; // 60 * 60 * 1000; // 60 minutes

    constructor() {
    }

    /**
     * Hämta cache-nyckel baserat på aktuell valuta/land.
     * Om moutife_country2-cookien finns, läggs den till i nyckeln
     * så att varje land/valuta får sin egen cache.
     */
    get cacheKey() {
        const country = this._getCookie('moutife_country2');
        return country ? `${this.cacheKeyBase}_${country}` : this.cacheKeyBase;
    }

    _getCookie(name) {
        const match = document.cookie.match(new RegExp('(?:^|; )' + name + '=([^;]*)'));
        return match ? decodeURIComponent(match[1]) : '';
    }

    // Funktion för att hitta bästa "från"-pris baserat på hierarkin
    async getFromPrice(paper, presentation = null, size = null) {
        await this.load();
        const fromPrices = this.data?.from_prices || null;
        if (!fromPrices) {
            return []; // Fallback om objektet inte finns
        }

        // Definiera nycklarna från mest specifik till mest generell (paper → presentation → size)
        const keySpecific = `${paper}_${presentation}_${size}`; // t.ex. "glossy_canvas_a4"
        const keyMedium = `${paper}_${presentation}`;       // t.ex. "glossy_canvas"
        const keyGeneral = paper;                    // t.ex. "glossy"

        // Leta efter ett värde i hierarkin (redan formaterat från PHP)
        const price = fromPrices[keySpecific] ||
            fromPrices[keyMedium] ||
            fromPrices[keyGeneral] ||
            '';

        return (price !== '') ? _('From %s', price) : '';
    }
    async getAttributeHierarchy(paper, presentation = null) {
        await this.load();
        const hierarchy = this.data?.attributes_hierarchy || {};
        if (!hierarchy[paper]) {
            Logger.warn("No attributes found for paper:", paper);
            return [];
        }
        if (presentation && hierarchy[paper][presentation]) {
            return Object.values(hierarchy[paper][presentation]) || [];
        }
        return Object.keys(hierarchy[paper]) || [];

    }
    async getAttributeHierarchyPresentations() {
        const paper = Storage.get('paper');
        if (!paper) {
            Logger.warn("No paper selected, cannot get attribute hierarchy presentations");
            return [];
        }
        return await this.getAttributeHierarchy(paper);
    }
    async getAttributeHierarchyProductIDs() {
        const paper = Storage.get('paper');
        const presentation = Storage.get('presentation');
        if (!paper || !presentation) {
            Logger.debug("No paper or presentation selected, cannot update AttrSizeList");
            return [];
        }
        return await this.getAttributeHierarchy(paper, presentation);
    }

    async getPresentations() {
        await this.load();
        return this.data?.attributes?.presentation?.terms || [];
    }
    async getPapers() {
        await this.load();
        return this.data?.attributes?.paper?.terms || [];
    }
    async getSizes() {
        await this.load();
        return this.data?.attributes?.size?.terms || [];
    }
    async getRetouchs() {
        await this.load();
        return this.data?.attributes.retouch?.terms || [];
    }
    async getRetouchProducts() {
        await this.load();
        return this.data?.retouchProducts || {};
    }
    async getProduct(productId) {
        await this.load();
        const products = this.data?.products || [];
        return products.find(p => p.value === productId) || null;
    }
    async getProducts() {
        await this.load();
        return this.data?.products || [];
    }
    async getProductPermalink(productValue) {
        await this.load();
        const products = this.data?.products || [];
        const product = products.find(p => p.value === productValue);
        if (product) {
            return product.permalink || '';
        }
        Logger.warn("Product with value %s not found", productValue);
        return '';
    }
    async getAttributes() {
        await this.load();
        return this.data?.attributes || {};
    }
    async getResolutions() {
        await this.load();
        return this.data?.resolution || {};
    }
    async getSizeResolution(size, presentation, orientation, paper = null) {
        const resolutions = await this.getResolutions();

        // Paper-specifik override (kan ha width/height, bleed, eller bada)
        const paperOverride = paper ? resolutions?.per_size_paper?.[paper]?.[size]?.[presentation] : null;

        // Default crop size
        const defaultRes = resolutions?.per_size?.[size]?.[presentation];

        // Mergea: paper-override tar företräde, fallback till default
        const width = paperOverride?.width || defaultRes?.width || null;
        const height = paperOverride?.height || defaultRes?.height || null;
        const bleed = paperOverride?.bleed ?? defaultRes?.bleed ?? 0;

        if (width && height) {
            return (orientation === 'portrait') ? [width, height, bleed] : [height, width, bleed];
        }

        // Fallback: parsa storlek fran slug
        const [fbWidth, fbHeight] = size?.split('x')
            .map(dim => parseInt(dim, 10) * 10) || [];
        if (isNaN(fbWidth) || isNaN(fbHeight)) {
            Logger.debug("Invalid size format: %s.", size);
            return null;
        }
        return (orientation === 'portrait') ? [fbWidth, fbHeight, 0] : [fbHeight, fbWidth, 0];
    }

    async clearIfNotValidPresentationPaperSizeCombinations(presentation = null, paper = null, size = null) {
        await this.load();
        const hierarchy = this.data?.attributes_hierarchy || {};
        const products = this.data?.products || [];

        // if any of the values is null, return true
        if (!presentation || !paper || !size) {
            return true;
        }

        const validPresentations = Object.keys(hierarchy?.[paper] || {});
        if (!validPresentations.includes(presentation)) {
            Storage.remove('presentation');
            Storage.remove('size');
            return true;
        }

        // find selectable products for paper and presentation (inverted order)
        const selectableProducts = hierarchy?.[paper]?.[presentation] || [];
        if (selectableProducts.length === 0) {
            Storage.remove('size');
            return true;
        }

        // get size values from products
        // selectableProducts = selectableProducts.map(p => p.value);

        // check variations[*].attributes.attribute_pa_size from each selectable product
        const selectableSizes = selectableProducts.map(prodId => {
            const prod = products.find(p => p.value === prodId);
            // find attribute_pa_size from each variation
            const variationSizes = prod?.variations?.map(variation => variation.attributes?.attribute_pa_size) || [];
            return variationSizes.length > 0 ? variationSizes : null;
        });

        // flatten array and remove duplicates
        const flattenedSizes = [...new Set(selectableSizes.flat())];

        if (!flattenedSizes.includes(size)) {
            Storage.remove('size');
            return true;
        }
        return true;
    }
    async clearIfNotValidCombination(type, value) {
        await this.load();
        const presentation = Storage.get('presentation');
        const paper = Storage.get('paper');
        const size = Storage.get('size');

        switch (type) {
            case 'presentation':
                return await this.clearIfNotValidPresentationPaperSizeCombinations(value, paper, size);
            case 'paper':
                return await this.clearIfNotValidPresentationPaperSizeCombinations(presentation, value, size);
            case 'size':
                return await this.clearIfNotValidPresentationPaperSizeCombinations(presentation, paper, value);
            default:
                return true;
        }
    }

    // async updateValidPaperSizeCombinations(presentation = null, paper = null, size = null) {
    //     await this.load();
    //     // TODO: implement logic to update valid paper-size combinations based on selected presentation
    //     const hierarchy = this.data?.attributes_hierarchy || {};
    //     if (!presentation) {
    //         presentation = Storage.get('presentation');
    //     }
    //     if (!paper) {
    //         paper = Storage.get('paper');
    //     }
    //     if (!size) {
    //         size = Storage.get('size');
    //     }
    //     let validSizes = hierarchy?.[presentation]?.[paper]
    //         ? Object.keys(hierarchy[presentation][paper])
    //         : [];

    //     // check if paper is valid
    //     if (paper && !Object.keys(hierarchy?.[presentation] || {}).includes(paper)) {
    //         Logger.warn("Current paper %s is not valid for presentation %s. Resetting paper and size.", paper, presentation);
    //         Storage.remove('paper');
    //         // get first valid paper
    //         const validPapers = Object.keys(hierarchy?.[presentation] || {});
    //         if (validPapers.length > 0) {
    //             const newPaper = validPapers[0];
    //             Storage.set('paper', newPaper);
    //             paper = newPaper;
    //             Logger.info("Paper reset to first valid paper: %s", newPaper);
    //             validSizes = hierarchy?.[presentation]?.[paper]
    //                 ? Object.keys(hierarchy[presentation][paper])
    //                 : [];
    //         }

    //     }

    //     // check if current size is valid
    //     if (size && !validSizes.includes(size)) {
    //         Logger.warn("Current size %s is not valid for presentation %s and paper %s. Resetting size.", size, presentation, paper);
    //         Storage.remove('size');
    //         // get first valid size
    //         if (validSizes.length > 0) {
    //             Storage.set('size', validSizes[0]);
    //             Logger.info("Size reset to first valid size: %s", validSizes[0]);
    //         } else {
    //             Logger.warn("No valid sizes available for presentation %s and paper %s.", presentation, paper);
    //         }
    //     }
    // }
    async getDPI(paper = null) {
        const resolutions = await this.getResolutions();
        const defaults = {
            'grade_1': 50,
            'grade_2': 100,
            'grade_3': 150,
            'grade_4': 200,
            'grade_5': 300
        };
        const globalDpi = resolutions?.dpi || defaults;

        // Paper-specifik override: mergea med global (paper tar företräde)
        if (paper && resolutions?.dpi_paper?.[paper]) {
            return { ...globalDpi, ...resolutions.dpi_paper[paper] };
        }
        return globalDpi;
    }

    async getFrameSettings() {
        await this.load();
        return this.data?.frame_settings || {};
    }

    async getDefaultFrame(paper, presentation) {
        const settings = await this.getFrameSettings();
        return settings?.default_frames?.[paper]?.[presentation] || null;
    }


    async load() {
        // return if already loaded
        if (this.loading) {
            // wait untir loading is finished before returning
            // Logger.warn('Products are already loading, waiting for completion...');
            // wait for 1 second before trying to load again, return when this.loading is false again
            while (this.loading) {
                Logger.debug('Waiting for products to load...');
                await new Promise(resolve => setTimeout(resolve, 1000));
            }
            return;
        }

        if (this.data) {
            return;
        }

        // Try session cache first
        try {
            const cachedRaw = sessionStorage.getItem(this.cacheKey);
            if (cachedRaw) {
                const cached = JSON.parse(cachedRaw);
                const cachedAt = Number(cached?.cachedAt || 0);
                if (cached?.data && cachedAt && (Date.now() - cachedAt) < this.cacheTtlMs) {
                    this.data = cached.data;
                    Logger.debug('Products loaded from cache:', this.data);
                    return;
                }
            }
        } catch (error) {
            Logger.debug('Products cache read failed:', error);
        }

        this.loading = true;
        // Fetch products from the server, retrying a few times before giving up.
        // A single transient failure (common on slow/flaky mobile networks) used
        // to leave this.data null and silently return, so a customizer step that
        // reads its options right after (e.g. presentation/size) rendered EMPTY —
        // making step 3 look skipped. Retrying keeps a blip from producing a
        // spurious empty step. On final failure data stays null so the next
        // caller re-attempts, and loadedOk stays false.
        const maxAttempts = 3;
        for (let attempt = 1; attempt <= maxAttempts; attempt++) {
            try {
                const response = await fetch('/wp-json/mscp/v1/products');
                const json = await response.json();
                if (!response.ok) {
                    throw new Error('Network response was not ok, ' + (json?.message || response.status));
                }
                Logger.debug('Products loaded from server:', json);
                this.data = json || {};
                try {
                    sessionStorage.setItem(this.cacheKey, JSON.stringify({
                        cachedAt: Date.now(),
                        data: this.data
                    }));
                } catch (error) {
                    Logger.debug('Products cache write failed:', error);
                }
                this.loading = false;
                return;
            } catch (error) {
                Logger.error(`Error fetching products (attempt ${attempt}/${maxAttempts}):`, error);
                if (attempt < maxAttempts) {
                    await new Promise(resolve => setTimeout(resolve, 400 * attempt));
                }
            }
        }
        this.loading = false; // gave up for now; data stays null so a later call retries
    }

    /** True once products have been successfully loaded (data present). */
    get loadedOk() {
        return this.data != null;
    }


}

const products = new Products();
export default products;